import { Flex, Table, Typography } from "antd";
import type { ColumnType } from "antd/lib/table";
import { ImpressaoContainer, ImpressaoHeader } from "components/Impressao";
import { useRelatorioStore } from "pages/relatorios/store";
import type { ApiRelProdutorFaixaLitragem } from "pages/relatorios/types/ApiRelProdutorFaixaLitragem";
import { useMemo } from "react";
import { Cell, Pie, PieChart, Legend as RechartsLegend, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { decimalFormater } from "std/format";
import { roundAbnt } from "std/math";
import { FAIXAS_LITRAGEM, getFaixaByVolume } from "./faixasLitragem";

const { Title, Text } = Typography;

type FaixaGroup = {
	id: string;
	label: string;
	min: number;
	max: number;
	produtores: ApiRelProdutorFaixaLitragem[];
	totalVolumeFaixa: number;
	qtdProdutores: number;
	percentualProdutores: number;
};

// limite mínimo de % para destacar uma faixa individualmente
const PERCENTUAL_MINIMO = 3;

const COLORS = [
	"#3366CC",
	"#DC3912",
	"#FF9900",
	"#109618",
	"#990099",
	"#3B3EAC",
	"#0099C6",
	"#DD4477",
	"#66AA00",
	"#B82E2E",
	"#316395",
	"#994499",
	"#22AA99",
];

const produtorColumns: ColumnType<ApiRelProdutorFaixaLitragem>[] = [
	{
		dataIndex: "produtorCodigo",
		key: "produtorCodigo",
		title: "Código",
		align: "center",
	},
	{
		dataIndex: "produtorNome",
		key: "produtorNome",
		title: "Produtor",
	},
	{
		dataIndex: "rotaIdentificacao",
		key: "rotaIdentificacao",
		title: "Rota",
	},
	{
		dataIndex: "totalVolume",
		key: "totalVolume",
		title: "Volume Total (L)",
		align: "right",
		render: (value: number) => decimalFormater(value || 0),
	},
];

type PieData = {
	id: string;
	label: string;
	qtdProdutores: number;
	percentualProdutores: number;
};

function renderCustomizedLabel(props: any) {
	const { cx, cy, midAngle, innerRadius, outerRadius, percent, name } = props;

	const percentual = percent * 100;

	// não desenha label nas fatias pequenas (exceto "Outros")
	if (percentual < PERCENTUAL_MINIMO && !String(name).startsWith("Outros")) {
		return null;
	}

	const RADIAN = Math.PI / 180;
	const radius = innerRadius + (outerRadius - innerRadius) * 1.1;
	const x = cx + radius * Math.cos(-midAngle * RADIAN);
	const y = cy + radius * Math.sin(-midAngle * RADIAN);

	return (
		<text x={x} y={y} textAnchor={x > cx ? "start" : "end"} dominantBaseline="central" fontSize={14}>
			{`${name} (${percentual.toFixed(1)}%)`}
		</text>
	);
}

function PieProdutorFaixaLitragem({ data }: { data: PieData[] }) {
	if (!data || data.length === 0) {
		return <Text>Nenhum dado para exibir no gráfico.</Text>;
	}

	return (
		<div style={{ width: "100%", height: 600 }}>
			<ResponsiveContainer>
				<PieChart>
					<Pie data={data} dataKey="qtdProdutores" nameKey="label" outerRadius={220} labelLine={false} label={renderCustomizedLabel}>
						{data.map((entry, index) => (
							<Cell key={entry.id} fill={COLORS[index % COLORS.length]} />
						))}
					</Pie>
					<RechartsTooltip
						formatter={(value: any, _name: any, entry: any) => {
							const qtd = Number(value || 0);
							const perc = entry?.payload?.percentualProdutores ?? 0;
							return [`${qtd} produtor(es)`, `${perc.toFixed(2)}%`];
						}}
						labelFormatter={(label) => String(label ?? "")}
					/>
					<RechartsLegend />
				</PieChart>
			</ResponsiveContainer>
		</div>
	);
}

export function TableRelProdutorFaixaLitragem({ registros, title }: { registros: ApiRelProdutorFaixaLitragem[]; title: string }) {
	const filter = useRelatorioStore((s) => s.filter);

	const totalProdutores = useMemo(() => (registros || []).length, [registros]);

	const faixasSelecionadasIds =
		filter?.filterFaixasIds && filter.filterFaixasIds.length > 0 ? filter.filterFaixasIds : FAIXAS_LITRAGEM.map((f) => f.id);

	const grupos: FaixaGroup[] = useMemo(() => {
		if (!registros || registros.length === 0) {
			return [];
		}

		const baseFaixas = FAIXAS_LITRAGEM.filter((faixa) => faixasSelecionadasIds.includes(faixa.id));

		const mapaProdutoresPorFaixa: {
			[id: string]: ApiRelProdutorFaixaLitragem[];
		} = {};

		baseFaixas.forEach((faixa) => {
			mapaProdutoresPorFaixa[faixa.id] = [];
		});

		registros.forEach((produtor) => {
			const faixa = getFaixaByVolume(produtor.totalVolume);
			if (!faixa) {
				return;
			}
			if (!faixasSelecionadasIds.includes(faixa.id)) {
				return;
			}

			const list = mapaProdutoresPorFaixa[faixa.id] || [];
			list.push(produtor);
			mapaProdutoresPorFaixa[faixa.id] = list;
		});

		const gruposCalculados: FaixaGroup[] = baseFaixas.map((faixa) => {
			const produtoresFaixa = mapaProdutoresPorFaixa[faixa.id] || [];
			const totalVolumeFaixa = roundAbnt(
				produtoresFaixa.reduce((acc, cur) => acc + Number(cur.totalVolume || 0), 0),
				4,
			);
			const qtdProdutores = produtoresFaixa.length;
			const percentualProdutores = totalProdutores > 0 ? (qtdProdutores / totalProdutores) * 100 : 0;

			return {
				id: faixa.id,
				label: faixa.label,
				min: faixa.min,
				max: faixa.max,
				produtores: produtoresFaixa,
				totalVolumeFaixa,
				qtdProdutores,
				percentualProdutores,
			};
		});

		return gruposCalculados;
	}, [registros, faixasSelecionadasIds, totalProdutores]);

	const totalVolumeGeral = useMemo(
		() =>
			roundAbnt(
				(registros || []).reduce((acc, cur) => acc + Number(cur.totalVolume || 0), 0),
				4,
			),
		[registros],
	);

	const totalProdutoresComFaixa = useMemo(() => grupos.reduce((acc, cur) => acc + cur.qtdProdutores, 0), [grupos]);

	const pieData: PieData[] = useMemo(() => {
		// lista base com todas as faixas que têm produtores
		const base = grupos
			.filter((g) => g.qtdProdutores > 0)
			.map((g) => ({
				id: g.id,
				label: g.label,
				qtdProdutores: g.qtdProdutores,
				percentualProdutores: g.percentualProdutores,
			}));

		if (base.length === 0) {
			return [];
		}

		const grandes = base.filter((g) => g.percentualProdutores >= PERCENTUAL_MINIMO);
		const pequenas = base.filter((g) => g.percentualProdutores < PERCENTUAL_MINIMO);

		const somaQtdOutros = pequenas.reduce((acc, cur) => acc + cur.qtdProdutores, 0);
		const somaPercOutros = pequenas.reduce((acc, cur) => acc + cur.percentualProdutores, 0);

		const data: PieData[] = [...grandes];

		if (somaQtdOutros > 0) {
			data.push({
				id: "OUTROS",
				label: `Outros (< ${PERCENTUAL_MINIMO}% )`,
				qtdProdutores: somaQtdOutros,
				percentualProdutores: somaPercOutros,
			});
		}

		return data;
	}, [grupos]);

	// Tabela de faixas (pai)
	const columnsFaixas: ColumnType<FaixaGroup>[] = [
		{
			dataIndex: "label",
			key: "label",
			title: "Faixa de Litragem (L)",
		},
		{
			dataIndex: "qtdProdutores",
			key: "qtdProdutores",
			title: "Qtd. Produtores",
			align: "right",
		},
		{
			dataIndex: "percentualProdutores",
			key: "percentualProdutores",
			title: "% Produtores",
			align: "right",
			render: (value: number) => `${value.toFixed(2)} %`,
		},
		{
			dataIndex: "totalVolumeFaixa",
			key: "totalVolumeFaixa",
			title: "Volume Total da Faixa (L)",
			align: "right",
			render: (value: number) => decimalFormater(value || 0),
		},
	];

	return (
		<ImpressaoContainer className="w-full mt-3" title={title}>
			<ImpressaoHeader title={title} />

			<Table<FaixaGroup>
				rowKey="id"
				columns={columnsFaixas}
				dataSource={grupos}
				pagination={false}
				size="small"
				rowClassName="zebra"
				expandable={{
					expandedRowRender: (record) => (
						<div style={{ paddingTop: "4px", paddingBottom: "8px" }}>
							<Table<ApiRelProdutorFaixaLitragem>
								rowKey={(row) => `${row.produtorId}-${row.rotaId || "rota"}`}
								bordered={true}
								columns={produtorColumns}
								dataSource={record.produtores}
								pagination={false}
								size="small"
								summary={(rows) => {
									const qtd = rows.length;
									const totalVolumeFaixa = roundAbnt(
										rows.reduce((acc, cur) => acc + Number(cur.totalVolume || 0), 0),
										4,
									);

									return (
										<Table.Summary.Row>
											<Table.Summary.Cell index={0} align="left" colSpan={2}>
												<Flex vertical={true}>
													<Title level={5} style={{ margin: "0px" }}>
														Registros: {qtd}
													</Title>
												</Flex>
											</Table.Summary.Cell>
											<Table.Summary.Cell index={1} align="right" colSpan={2}>
												<Flex vertical={true}>
													<Title level={5} style={{ margin: "0px" }}>
														Total Faixa: {decimalFormater(totalVolumeFaixa)}
													</Title>
												</Flex>
											</Table.Summary.Cell>
										</Table.Summary.Row>
									);
								}}
							/>
						</div>
					),
					defaultExpandAllRows: true,
				}}
				summary={() => (
					<Table.Summary.Row>
						<Table.Summary.Cell index={0} align="left" colSpan={2}>
							<Flex vertical={true}>
								<Title level={4} style={{ margin: "0px" }}>
									<b>Produtores (com faixa): {totalProdutoresComFaixa}</b>
								</Title>
							</Flex>
						</Table.Summary.Cell>
						<Table.Summary.Cell index={1} align="right" colSpan={2}>
							<Flex vertical={true}>
								<Title level={4} style={{ margin: "0px" }}>
									<b>Volume Total Geral: {decimalFormater(totalVolumeGeral)}</b>
								</Title>
							</Flex>
						</Table.Summary.Cell>
					</Table.Summary.Row>
				)}
			/>

			<div className="relatorio-pizza-print-page">
				<PieProdutorFaixaLitragem data={pieData} />
			</div>
		</ImpressaoContainer>
	);
}
